import React, { useState, useEffect, useRef } from "react";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimes,
  FaSpinner,
} from "react-icons/fa";

type ExamSubmitConfirmationProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  totalQuestions: number;
  answeredQuestions: number;
  isSubmitting?: boolean;
  timeRemaining?: number;
};

const ExamSubmitConfirmation: React.FC<ExamSubmitConfirmationProps> = ({
  isOpen,
  onClose,
  onConfirm,
  totalQuestions,
  answeredQuestions,
  isSubmitting = false,
  timeRemaining,
}) => {
  // ✅ CRITICAL: State for checkbox confirmations
  const [confirmedReview, setConfirmedReview] = useState(false);
  const [confirmedFinal, setConfirmedFinal] = useState(false);

  // ✅ CRITICAL: Add ref to prevent double-clicks on confirm button
  const confirmingRef = useRef(false);

  // ✅ CRITICAL: Reset checkboxes and ref when modal opens
  useEffect(() => {
    if (isOpen) {
      setConfirmedReview(false);
      setConfirmedFinal(false);
      confirmingRef.current = false;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const unansweredCount = totalQuestions - answeredQuestions;

  // ✅ CRITICAL: Only allow submit if BOTH checkboxes are checked and not already confirming
  const canSubmit =
    confirmedReview &&
    confirmedFinal &&
    !isSubmitting &&
    !confirmingRef.current;

  const formatTime = (seconds?: number) => {
    if (!seconds || seconds === 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // ✅ CRITICAL: Prevent submission if checkboxes not checked or already confirming
  const handleConfirm = () => {
    // Prevent double-clicks
    if (confirmingRef.current) {
      console.log("Already confirming, ignoring click");
      return;
    }

    if (!confirmedReview || !confirmedFinal) {
      alert("⚠️ Please check both confirmation boxes before submitting!");
      return;
    }

    // ✅ Set ref immediately to prevent double-clicks
    confirmingRef.current = true;
    onConfirm();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
          <h3 className="text-base sm:text-xl font-bold text-gray-900">
            Submit Exam Confirmation
          </h3>
          <button
            onClick={onClose}
            disabled={isSubmitting || confirmingRef.current}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <FaTimes className="text-lg sm:text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 py-4 sm:py-6">
          {/* Warning Icon */}
          <div className="flex justify-center mb-3 sm:mb-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-orange-100 rounded-full flex items-center justify-center">
              <FaExclamationTriangle className="text-orange-600 text-2xl sm:text-3xl" />
            </div>
          </div>

          {/* Main Message */}
          <div className="text-center mb-4 sm:mb-6">
            <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-2">
              Are you sure you want to submit?
            </h4>
            <p className="text-xs sm:text-sm text-gray-600">
              Once submitted, you cannot change your answers.
            </p>
          </div>

          {/* Progress Summary */}
          <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
            {/* Answered Questions */}
            <div className="flex items-center justify-between p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-600 rounded-lg flex items-center justify-center">
                  <FaCheckCircle className="text-white text-sm sm:text-lg" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-semibold text-green-800">
                    Answered Questions
                  </p>
                  <p className="text-xs text-green-600 hidden sm:block">
                    You've completed these
                  </p>
                </div>
              </div>
              <div className="text-xl sm:text-2xl font-bold text-green-600">
                {answeredQuestions}
              </div>
            </div>

            {/* Unanswered Questions */}
            {unansweredCount > 0 && (
              <div className="flex items-center justify-between p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-600 rounded-lg flex items-center justify-center">
                    <FaExclamationTriangle className="text-white text-sm sm:text-lg" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-semibold text-red-800">
                      Unanswered Questions
                    </p>
                    <p className="text-xs text-red-600 hidden sm:block">
                      These will be marked as blank
                    </p>
                  </div>
                </div>
                <div className="text-xl sm:text-2xl font-bold text-red-600">
                  {unansweredCount}
                </div>
              </div>
            )}

            {/* Time Remaining */}
            {timeRemaining !== undefined && timeRemaining > 0 && (
              <div className="p-2.5 sm:p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-blue-800">
                    Time Remaining:
                  </span>
                  <span className="text-xs sm:text-sm font-bold text-blue-600">
                    {formatTime(timeRemaining)}
                  </span>
                </div>
                <p className="text-xs text-blue-600 mt-1">
                  You can continue working if needed
                </p>
              </div>
            )}
          </div>

          {/* Warning Message */}
          {unansweredCount > 0 && (
            <div className="p-3 sm:p-4 bg-orange-50 border-l-4 border-orange-500 rounded-r-lg mb-4 sm:mb-6">
              <p className="text-xs sm:text-sm text-orange-800">
                <strong>Warning:</strong> You have {unansweredCount} unanswered{" "}
                {unansweredCount === 1 ? "question" : "questions"}. These will
                receive 0 points. Consider reviewing your answers before
                submitting.
              </p>
            </div>
          )}

          {/* ✅ CRITICAL: Confirmation Checklist with controlled inputs */}
          <div className="space-y-3 mb-4 sm:mb-6 bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
            <p className="text-sm font-bold text-yellow-900 mb-3">
              ⚠️ Required Confirmations:
            </p>

            <label className="flex items-start gap-3 cursor-pointer hover:bg-yellow-100 p-3 rounded-lg transition-colors border-2 border-transparent hover:border-yellow-300">
              <input
                type="checkbox"
                checked={confirmedReview}
                onChange={(e) => setConfirmedReview(e.target.checked)}
                disabled={isSubmitting || confirmingRef.current}
                className="mt-1 w-5 h-5 text-[#009fe3] focus:ring-[#009fe3] rounded cursor-pointer flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <span className="text-xs sm:text-sm text-gray-700 font-medium">
                I confirm that I have reviewed my answers and am ready to submit
                this exam.
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer hover:bg-yellow-100 p-3 rounded-lg transition-colors border-2 border-transparent hover:border-yellow-300">
              <input
                type="checkbox"
                checked={confirmedFinal}
                onChange={(e) => setConfirmedFinal(e.target.checked)}
                disabled={isSubmitting || confirmingRef.current}
                className="mt-1 w-5 h-5 text-[#009fe3] focus:ring-[#009fe3] rounded cursor-pointer flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <span className="text-xs sm:text-sm text-gray-700 font-medium">
                I understand that once submitted, I cannot change my answers.
              </span>
            </label>
          </div>

          {/* ✅ Warning if checkboxes not checked */}
          {!canSubmit && !isSubmitting && !confirmingRef.current && (
            <div className="p-3 bg-red-50 border-2 border-red-400 rounded-lg mb-3 animate-pulse">
              <p className="text-xs sm:text-sm text-red-800 text-center font-bold flex items-center justify-center gap-2">
                <FaExclamationTriangle />
                You must check both boxes above to submit!
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            disabled={isSubmitting || confirmingRef.current}
            className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base order-2 sm:order-1"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canSubmit}
            className={`flex-1 flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-200 text-sm sm:text-base order-1 sm:order-2 ${
              canSubmit
                ? "bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white hover:shadow-lg cursor-pointer"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
            title={
              !canSubmit ? "Check both boxes to enable submit" : "Submit exam"
            }
          >
            {isSubmitting || confirmingRef.current ? (
              <>
                <FaSpinner className="animate-spin text-sm sm:text-base" />
                Submitting...
              </>
            ) : (
              <>
                <FaCheckCircle className="text-sm sm:text-base" />
                Yes, Submit Exam
              </>
            )}
          </button>
        </div>

        {/* Additional Info */}
        <div className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-100 rounded-b-xl text-center">
          <p className="text-xs text-gray-600">
            Your exam will be saved and sent for grading immediately after
            submission.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExamSubmitConfirmation;
